import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import SectionHeading from './SectionHeading';
import { trackEvent } from '../utils/analytics';
import Icon from './Icon';
import { PHONE, TELEGRAM, VIBER } from '../content/contacts';

const API_URL = process.env.REACT_APP_API_URL || '/api/contact';

const FIELDS = ['name', 'phone', 'message'];
const FIELD_LABELS = { name: 'ім’я', phone: 'телефон', message: 'опис ситуації' };

// Ті самі межі, що перевіряє сервер. Без maxLength людина дізнавалася б про
// межу вже після відправлення, коли текст написано.
const MAX_LENGTH = { name: 100, phone: 32, message: 5000 };

// Спільний об'єкт початкового стану: за тотожністю з ним ефект чернетки
// відрізняє «форму ще не чіпали» від «людина стерла все сама».
const EMPTY_FORM = { name: '', phone: '', message: '' };

/**
 * Телефон приймаємо в будь-якому написанні: «+38 (067) 123-45-67»,
 * «0671234567», «38067…». Чіпляємось лише до кількості цифр — усе інше
 * людина пише як звикла, і відхиляти її за формат означає втратити заявку
 * там, де достатньо прибрати дужки на нашому боці. 10 цифр — це «0671234567»
 * без коду країни, 15 — межа E.164; так само рахує сервер.
 */
const digitsOf = (value) => String(value).replace(/\D/g, '');

const validate = ({ name, phone }) => {
  const errors = {};

  const cleanName = name.trim();
  if (!cleanName) errors.name = 'Вкажіть, будь ласка, ім’я.';
  else if (cleanName.length < 2) errors.name = 'Ім’я закоротке — потрібно хоча б дві літери.';

  const digits = digitsOf(phone);
  if (!phone.trim()) errors.phone = 'Вкажіть номер телефону, щоб ми могли передзвонити.';
  else if (digits.length < 10) errors.phone = 'У номері бракує цифр. Приклад: 067 123 45 67.';
  else if (digits.length > 15) errors.phone = 'У номері забагато цифр — перевірте, чи не повторився код країни.';

  return errors;
};

/*
 * Що показати, коли сервер відхилив поле, а браузерна перевірка його
 * пропустила (правила розійшлися або запит підробили). Текст сервера
 * не показуємо як є: людині потрібна підказка, що виправити, а не
 * повідомлення валідатора.
 */
const SERVER_FIELD_ERRORS = {
  name: 'Перевірте ім’я: від 2 до 100 символів.',
  phone: 'Перевірте номер: у ньому має бути від 10 до 15 цифр.',
  message: 'Опис задовгий — скоротіть його до 5000 символів.'
};

const DIRECT = 'Або зв’яжіться з нами напряму:';

const FAILURES = {
  offline: {
    title: 'Не вдалося надіслати заявку',
    text: `Схоже, зникло з’єднання з інтернетом. Усе, що ви ввели, лишилося у формі — спробуйте ще раз. ${DIRECT}`
  },
  server: {
    title: 'Не вдалося надіслати заявку',
    text: `На нашому боці стався збій. Усе, що ви ввели, лишилося у формі — спробуйте ще раз трохи згодом. ${DIRECT}`
  },
  rejected: {
    title: 'Не вдалося надіслати заявку',
    text: `Перевірте ім’я та номер телефону й надішліть ще раз. ${DIRECT}`
  }
};

const tooManyAttempts = (minutes) => ({
  title: 'Забагато спроб за короткий час',
  text: `Спробуйте ще раз ${minutes ? `за ${minutes} хв` : 'трохи згодом'}. ${DIRECT}`
});

/** Retry-After у секундах → цілі хвилини; якщо заголовка немає — null. */
const retryMinutes = (res) => {
  const seconds = Number(res.headers.get('Retry-After'));
  return Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds / 60) : null;
};

/*
 * Чернетка у sessionStorage: перезавантаження сторінки чи випадковий перехід
 * за посиланням не мають губити написаний текст. Саме session-, а не
 * localStorage — дані не переживають закриття вкладки й не лишаються на
 * чужому комп'ютері. Сховище буває недоступне (приватний режим, заборонені
 * cookies), тому кожне звернення в try/catch: без чернетки форма працює так само.
 *
 * startedAt — коли людина почала заповнювати. Антиспам на сервері вважає
 * ботом усе, що заповнене швидше за 4 секунди, і тихо відкидає, тож
 * відновлена чернетка, надіслана одразу після перезавантаження, інакше
 * зникла б як спам.
 */
const DRAFT_KEY = 'vmg-contact-draft';

const readDraft = () => {
  try {
    const draft = JSON.parse(window.sessionStorage.getItem(DRAFT_KEY) || 'null');
    return draft && typeof draft === 'object' ? draft : null;
  } catch {
    return null;
  }
};

const writeDraft = (data, startedAt) => {
  try {
    if (FIELDS.every((key) => !data[key])) {
      window.sessionStorage.removeItem(DRAFT_KEY);
    } else {
      window.sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ ...data, startedAt }));
    }
  } catch {
    // Сховище заповнене або заборонене — лишаємося без чернетки.
  }
};

const clearDraft = () => {
  try {
    window.sessionStorage.removeItem(DRAFT_KEY);
  } catch {
    // див. writeDraft
  }
};

/** Момент відкриття сторінки, а не монтування React. */
const pageOpenedAt = () => {
  try {
    return Math.round(window.performance.timeOrigin || window.performance.timing.navigationStart) || Date.now();
  } catch {
    return Date.now();
  }
};

/**
 * Фокус із прокруткою. Шапка сайту фіксована й закриває верх екрана, тож
 * звичайний focus() міг поставити поле з помилкою рівно під неї.
 */
const reveal = (el) => {
  const { top, bottom } = el.getBoundingClientRect();
  if (top < 96 || bottom > window.innerHeight) el.scrollIntoView({ block: 'center' });
  el.focus({ preventScroll: true });
};

/**
 * Поле з підписом, що лежить над рядком, а не всередині нього. Плаваючий
 * підпис (placeholder-transparent + peer-placeholder-shown) тут зайвий: він
 * економить висоту ціною того, що заповнене поле більше не каже, що в ньому
 * лежить.
 *
 * Зірочку ховаємо від диктора: обов'язковість він і так оголошує з атрибута
 * required, а «зірочка» вголос — просто шум.
 */
const Field = ({ id, label, required, hint, error, children }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-slate-700 mb-1.5">
      {label}
      {required && <span className="text-teal-700" aria-hidden="true"> *</span>}
    </label>
    {children}
    {error && (
      <p id={`${id}-error`} className="mt-1.5 flex items-start gap-1.5 text-sm text-red-700">
        <Icon name="fa-exclamation-circle" className="mt-0.5 flex-shrink-0" />
        <span>{error}</span>
      </p>
    )}
    {hint && <p id={`${id}-hint`} className="text-xs text-slate-600 mt-1.5">{hint}</p>}
  </div>
);

/** Зв'язок поля з його помилкою та підказкою — те, що диктор прочитає після назви. */
const describedBy = (id, { error, hint }) =>
  [error && `${id}-error`, hint && `${id}-hint`].filter(Boolean).join(' ') || undefined;

const inputClass = (invalid) =>
  'w-full rounded-lg border bg-white px-4 py-3 text-slate-900 placeholder-slate-500 ' +
  'focus:outline-none focus:ring-1 transition-colors duration-200 ' +
  (invalid
    ? 'border-red-600 focus:border-red-600 focus:ring-red-600'
    : 'border-slate-300 focus:border-teal-600 focus:ring-teal-600');

/**
 * Прямі канали на випадок, коли форма не спрацювала: людина, якій щойно
 * відмовив сайт, не має шукати номер деінде на сторінці.
 */
const FailureNotice = ({ failure, noticeRef }) => (
  <div
    ref={noticeRef}
    tabIndex={-1}
    className="mt-5 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-700"
  >
    <p className="flex items-start gap-2 font-semibold">
      <Icon name="fa-exclamation-circle" className="mt-0.5 flex-shrink-0" />
      <span>{failure.title}</span>
    </p>
    <p className="mt-1.5 leading-relaxed">{failure.text}</p>
    <p className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
      <a
        href={PHONE.href}
        onClick={() => trackEvent('contact_channel', 'contact-form-error-phone')}
        className="inline-flex items-center gap-2 font-semibold text-red-900 underline underline-offset-2 hover:no-underline"
      >
        <Icon name="fa-phone" />
        {PHONE.label}
      </a>
      <a
        href={TELEGRAM.href}
        onClick={() => trackEvent('contact_channel', 'contact-form-error-telegram')}
        className="inline-flex items-center gap-2 font-semibold text-red-900 underline underline-offset-2 hover:no-underline"
      >
        <Icon name={TELEGRAM.icon} />
        {`Написати в ${TELEGRAM.label}`}
      </a>
    </p>
  </div>
);

/**
 * Прямий зв'язок поруч із формою — лише на головній.
 *
 * До цього тут стояла картка з фото під вуаллю й номером на ній: ще один
 * прямокутник поруч із прямокутником форми. Тепер секція темна, і прямі
 * контакти стоять просто текстом ліворуч від форми: великий номер, два
 * месенджери, рядок про дистанційну роботу. Номер тут один раз; месенджери —
 * назвами, бо номер у них той самий.
 *
 * На сторінці /kontakty ця частина не потрібна — там прямі контакти вже
 * стоять у власному блоці над формою, тож Contact малює її лише на головній.
 */
const DirectContact = () => (
  <div className="mt-10 pt-8 border-t border-slate-200">
    <div className="flex items-center gap-3 mb-3">
      <span className="w-8 h-px bg-gradient-to-r from-teal-500 to-sky-400 flex-shrink-0"></span>
      <span className="text-teal-700 uppercase tracking-[0.2em] text-[0.7rem] font-semibold">
        Або просто зателефонуйте
      </span>
    </div>

    <a
      href={PHONE.href}
      onClick={() => trackEvent('contact_channel', 'contact-section-phone')}
      className="inline-block text-2xl sm:text-3xl lg:text-4xl font-extrabold font-display text-slate-900 hover:text-teal-800 transition-colors duration-200"
    >
      {PHONE.label}
    </a>

    <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2">
      {[TELEGRAM, VIBER].map((channel) => (
        <a
          key={channel.label}
          href={channel.href}
          onClick={() => trackEvent('contact_channel', `contact-section-${channel.label.toLowerCase()}`)}
          className="inline-flex items-center gap-2 text-slate-600 font-medium hover:text-teal-800 transition-colors duration-200"
        >
          <Icon name={channel.icon} className="text-teal-700" />
          {channel.label}
        </a>
      ))}
    </div>

  </div>
);

/**
 * `bare` — секція без власної шапки. На головній контакти йдуть після інших
 * секцій і потребують заголовка; на сторінці /kontakty заголовок уже дає
 * PageShell, і другий великий «Контакти» під першим виглядав би помилкою.
 *
 * Форма спрощена двічі. Спершу з майстра на три кроки — до одного екрана. Тепер
 * із п'яти полів до трьох: пошта й «заклад або місто» пішли. Обидва були
 * необов'язковими, обидва питали те, що все одно з'ясовується в першій розмові,
 * і обидва подовжували анкету рівно там, де людина вирішує, надсилати їй чи ні.
 *
 * На головній секція світло-сіра, а форма — біла картка з тінню: тут
 * побувала й темна редакція, але власник попросив прибрати контраст між
 * світлим і темним узагалі, і форма на темному пішла разом із темним
 * підвалом. Розкладка та сама: заголовок і прямі контакти ліворуч, форма
 * праворуч. Під самою формою при цьому тихо: візерунок тла згасає до
 * низу й не заходить під поля вводу. На /kontakty (`bare`) форма стоїть
 * сама на білому — там заголовок і контакти дає сама сторінка.
 */
const Contact = ({ bare }) => {
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });
  const location = useLocation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  // Помилка відправлення не зникає сама: у ній номер і Telegram, і людина
  // має встигнути ними скористатися. Прибирає її лише наступна спроба.
  const [failure, setFailure] = useState(null);
  // Поля, які відхилив сервер. Свої помилки поле втрачає, щойно його правлять.
  const [serverErrors, setServerErrors] = useState({});
  // Показуємо помилку поля лише після спроби відправити: підсвічувати
  // порожній телефон, поки людина ще друкує ім'я, — це докір ні за що.
  const [showErrors, setShowErrors] = useState(false);
  // Куди перевести фокус після наступного рендеру. Саме після: якби фокус
  // стрибав одразу в обробнику, диктор прочитав би поле ще без aria-invalid
  // і без тексту помилки.
  const [focusTarget, setFocusTarget] = useState(null);

  const formRef = useRef(null);
  const nameRef = useRef(null);
  const phoneRef = useRef(null);
  const messageRef = useRef(null);
  const successRef = useRef(null);
  const failureRef = useRef(null);
  const submittingRef = useRef(false);

  const formStartedRef = useRef(false);
  // Які поля вже позначені заповненими — щоб не слати подію на кожен клік
  // повз поле.
  const filledRef = useRef(new Set());
  // Час відкриття форми: бот відправляє миттєво, людині потрібні секунди
  const openedAtRef = useRef(Date.now());
  // Пастка для ботів: поле сховане від людей, але автозаповнювачі його бачать
  const [honeypot, setHoneypot] = useState('');

  const fieldErrors = { ...serverErrors, ...(showErrors ? validate(formData) : {}) };

  /*
   * Текст, набраний до гідрації. Сторінка приходить готовим HTML, і поля
   * приймають введення ще до завантаження JS; React під час гідрації лишає
   * цей текст у DOM, але стан компонента порожній. Без цього кроку
   * відправлення казало «вкажіть ім'я» людині, яка його бачить у полі, а
   * перший же символ в іншому полі стирав написане.
   *
   * Тут же відновлюється чернетка. Текст із DOM свіжіший, тож він важливіший.
   */
  useEffect(() => {
    const form = formRef.current;
    if (!form) return;
    const domValue = (key) => form.elements.namedItem(key)?.value || '';

    const draft = readDraft();
    const restored = {};
    let typedBeforeHydration = false;
    let fromDraft = false;

    FIELDS.forEach((key) => {
      const typed = domValue(key);
      const saved = typeof draft?.[key] === 'string' ? draft[key].slice(0, MAX_LENGTH[key]) : '';
      if (typed) typedBeforeHydration = true;
      else if (saved) fromDraft = true;
      restored[key] = typed || saved;
    });

    const trap = domValue('website');
    if (trap) setHoneypot(trap);

    if (typedBeforeHydration) {
      openedAtRef.current = Math.min(openedAtRef.current, pageOpenedAt());
      if (!formStartedRef.current) trackEvent('form_start');
    }
    if (fromDraft && Number.isFinite(draft.startedAt)) {
      openedAtRef.current = Math.min(openedAtRef.current, draft.startedAt);
    }
    if (typedBeforeHydration || fromDraft) {
      formStartedRef.current = true;
      setFormData(restored);
    }
  }, []);

  /*
   * Результат відправлення без JavaScript. Сервер відповідає на звичайний
   * POST переадресацією на /kontakty/?zayavka=ok або ?zayavka=pomylka, і
   * саме тут людина має побачити, чим скінчилося. Параметр одразу прибираємо
   * з адреси: оновлення сторінки не повинне вдруге казати «заявку отримано».
   */
  useEffect(() => {
    const outcome = new URLSearchParams(location.search).get('zayavka');
    if (outcome !== 'ok' && outcome !== 'pomylka') return;

    if (outcome === 'ok') {
      clearDraft();
      setFormData(EMPTY_FORM);
      setIsSubmitted(true);
      setFocusTarget({ ref: successRef });
    } else {
      setFailure(FAILURES.rejected);
      setFocusTarget({ ref: failureRef });
    }
    navigate({ pathname: location.pathname, hash: location.hash }, { replace: true });
  }, [location.search, location.pathname, location.hash, navigate]);

  useEffect(() => {
    if (formData === EMPTY_FORM) return;
    writeDraft(formData, openedAtRef.current);
  }, [formData]);

  useEffect(() => {
    const el = focusTarget?.ref.current;
    if (el) reveal(el);
  }, [focusTarget]);

  const fieldRefs = { name: nameRef, phone: phoneRef, message: messageRef };
  const focusField = (key) => setFocusTarget({ ref: fieldRefs[key] });

  /**
   * Позначка «це поле заповнили» — на виході з поля, якщо в ньому щось є.
   *
   * Надсилаємо назву поля і кількість символів. Самого тексту не надсилаємо
   * ніколи: людина, яка передумала і не натиснула «надіслати», свідомо не
   * дала нам цих даних, і забирати їх потайки не можна — ні юридично, ні
   * по-людськи. Довжина ж відповідає на те, заради чого це все: три символи
   * в телефоні означають «почав і кинув», тринадцять — «дописав до кінця і
   * все одно не надіслав», і це різні проблеми.
   */
  const handleBlur = (e) => {
    const { name, value } = e.target;
    const text = String(value || '').trim();
    if (!text || filledRef.current.has(name)) return;
    filledRef.current.add(name);
    trackEvent('form_field', `${FIELD_LABELS[name] || name}:${text.length}`);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (!formStartedRef.current) {
      formStartedRef.current = true;
      trackEvent('form_start');
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    if (serverErrors[name]) {
      setServerErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  /** Відповідь сервера з помилкою → що саме сказати людині. */
  const handleRejection = async (res) => {
    if (res.status === 400) {
      let body = {};
      try {
        body = await res.json();
      } catch {
        // тіло не JSON — лишаємося із загальним повідомленням
      }
      const fields = Array.isArray(body.fields) ? body.fields : [];
      const errors = {};
      fields.forEach((key) => {
        if (SERVER_FIELD_ERRORS[key]) errors[key] = SERVER_FIELD_ERRORS[key];
      });
      const first = FIELDS.find((key) => errors[key]);
      if (first) {
        setServerErrors(errors);
        focusField(first);
      } else {
        setFailure(FAILURES.rejected);
      }
      return;
    }

    // Сервер приймає до 20 КБ, а довгим за межами maxLength буває лише опис.
    if (res.status === 413) {
      setServerErrors({ message: 'Опис задовгий — скоротіть його й надішліть ще раз.' });
      focusField('message');
      return;
    }

    setFailure(res.status === 429 ? tooManyAttempts(retryMinutes(res)) : FAILURES.server);
  };

  const handleSubmit = async (e) => {
    // Без JS форма йде звичайним POST на ту саму адресу; з JS — надсилаємо
    // JSON самі, щоб лишитися на сторінці й показати результат на місці.
    e.preventDefault();
    if (submittingRef.current) return;

    setShowErrors(true);
    setFailure(null);

    const firstInvalid = FIELDS.find((key) => validate(formData)[key]);
    if (firstInvalid) {
      focusField(firstInvalid);
      return;
    }

    submittingRef.current = true;
    setIsSubmitting(true);
    setServerErrors({});

    try {
      const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          website: honeypot,
          elapsed: Date.now() - openedAtRef.current
        })
      });

      if (res.ok) {
        clearDraft();
        setFormData(EMPTY_FORM);
        setShowErrors(false);
        setIsSubmitted(true);
        setFocusTarget({ ref: successRef });
        trackEvent('form_submit');
      } else {
        await handleRejection(res);
      }
    } catch {
      setFailure(FAILURES.offline);
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  /*
   * Раніше підтвердження саме ховалося через 6 секунд. Тепер воно стоїть,
   * доки людина сама не захоче написати ще: фокус на ньому, і зникни воно
   * з-під курсора диктора — той опинився б на початку сторінки.
   */
  const startOver = () => {
    setIsSubmitted(false);
    focusField('name');
  };

  const nameError = fieldErrors.name;
  const phoneError = fieldErrors.phone;
  const messageError = fieldErrors.message;
  const messageHint = 'На якому ви етапі, що вже зроблено, чи є ліцензія й приміщення.';

  // На /kontakty заголовок форми — один із головних розділів сторінки (h2),
  // на головній він стоїть під секцією «Контакти» (h3).
  const FormTitle = bare ? 'h2' : 'h3';

  // method і action — для того, хто натиснув кнопку до завантаження JS (або
  // в кого JS не працює взагалі). Без них браузер відправляв форму
  // GET-запитом на поточну адресу: ім'я й телефон опинялися в адресному
  // рядку, історії браузера й логах сервера, а заявка не доходила.
  const form = (
    <form
      ref={formRef}
      method="post"
      action={API_URL}
      className={`relative rounded-2xl border border-slate-200 bg-white p-5 sm:p-7 lg:p-8 ${
        bare ? '' : 'lg:col-span-7 shadow-md'
      }`}
      onSubmit={handleSubmit}
      noValidate
    >
      {/* Під підтвердженням поля стають invisible: інакше Tab і диктор
          ходили б по формі, якої людина не бачить. */}
      <div className={isSubmitted ? 'invisible' : undefined}>
        {/*
          Пастка для ботів. Виносимо за екран, а не ховаємо через display:none —
          частина ботів пропускає приховані поля, але заповнює винесені.
          Для людини й скрінрідера поле не існує.
        */}
        <div aria-hidden="true" className="absolute -left-[9999px] top-auto w-px h-px overflow-hidden">
          <label htmlFor="website">Не заповнюйте це поле</label>
          <input
            id="website"
            name="website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
          />
        </div>

        <div className="mb-6">
          <FormTitle className="text-xl font-bold font-display text-slate-900">Залишіть заявку</FormTitle>
          <p className="text-slate-500 text-sm mt-1">
            Досить імені й телефону — решту з’ясуємо в розмові.
          </p>
        </div>

        <div className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field id="name" label="Ім’я" required error={nameError}>
              <input
                ref={nameRef}
                id="name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                onBlur={handleBlur}
                required
                aria-required="true"
                maxLength={MAX_LENGTH.name}
                autoComplete="name"
                className={inputClass(Boolean(nameError))}
                aria-invalid={nameError ? true : undefined}
                aria-describedby={describedBy('name', { error: nameError })}
              />
            </Field>

            <Field id="phone" label="Телефон" required error={phoneError}>
              <input
                ref={phoneRef}
                id="phone"
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                onBlur={handleBlur}
                required
                aria-required="true"
                maxLength={MAX_LENGTH.phone}
                autoComplete="tel"
                inputMode="tel"
                placeholder="+38 (0__) ___-__-__"
                className={inputClass(Boolean(phoneError))}
                aria-invalid={phoneError ? true : undefined}
                aria-describedby={describedBy('phone', { error: phoneError })}
              />
            </Field>
          </div>

          <Field id="message" label="Коротко про ситуацію" hint={messageHint} error={messageError}>
            <textarea
              ref={messageRef}
              id="message"
              name="message"
              rows={3}
              value={formData.message}
              onChange={handleInputChange}
              onBlur={handleBlur}
              maxLength={MAX_LENGTH.message}
              className={`${inputClass(Boolean(messageError))} resize-none`}
              aria-invalid={messageError ? true : undefined}
              aria-describedby={describedBy('message', { error: messageError, hint: messageHint })}
            />
          </Field>

        </div>

        <div className="mt-7 flex flex-col sm:flex-row sm:items-center gap-4">
          {/*
            aria-disabled замість disabled: вимкнена кнопка втрачає фокус,
            і людина з клавіатурою чи диктором опинялася на початку
            сторінки якраз тоді, коли мав з'явитися результат. Повторне
            натискання однаково відсікає submittingRef.

            Фон teal-700, а не teal-500: білий текст на teal-500 має
            контраст 2,5:1 при нормі 4,5:1, на teal-700 — 5,5:1.
          */}
          <button
            type="submit"
            aria-disabled={isSubmitting || undefined}
            className={`rounded-lg px-8 py-3.5 font-semibold text-sm text-white transition-colors duration-300 flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-700 focus-visible:ring-offset-2 ${
              isSubmitting
                ? 'bg-teal-800 cursor-wait'
                : 'bg-teal-700 hover:bg-teal-800 focus-visible:bg-teal-800'
            }`}
          >
            {isSubmitting ? (
              <>
                <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin mr-3" aria-hidden="true"></span>
                Надсилаємо…
              </>
            ) : (
              <>
                Надіслати заявку
                <Icon name="fa-paper-plane" className="ml-3" />
              </>
            )}
          </button>

        </div>

        {/* Область стоїть у DOM завжди, порожньою: диктор оголошує зміну
            всередині наявної області, а не щойно вставлену область. */}
        <div role="alert">
          {failure && <FailureNotice failure={failure} noticeRef={failureRef} />}
        </div>
      </div>

      <div aria-live="polite">
        {isSubmitted && (
          <div className="absolute inset-0 rounded-2xl bg-white/95 backdrop-blur-sm flex items-center justify-center p-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-5">
                <Icon name="fa-check" className="text-white text-2xl" />
              </div>

              <h3
                ref={successRef}
                tabIndex={-1}
                className="text-xl font-bold font-display mb-2 text-slate-900 focus:outline-none"
              >
                Заявку отримано
              </h3>
              <p className="text-slate-600 text-sm mt-2">
                Якщо питання термінове — телефонуйте, відповімо одразу.
              </p>
              <button
                type="button"
                onClick={startOver}
                className="mt-5 rounded text-sm font-semibold text-teal-700 underline underline-offset-2 hover:text-teal-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-700 focus-visible:ring-offset-2"
              >
                Надіслати ще одну заявку
              </button>
            </div>
          </div>
        )}
      </div>
    </form>
  );

  // На /kontakty сторінка сама розкладає блоки в сітку, тож форма йде без
  // власної секції: так її можна поставити в колонку поруч із каналами.
  if (bare) {
    return (
      <div id="contact" ref={ref} className="scroll-mt-28">
        {form}
      </div>
    );
  }

  return (
    <section id="contact" className="py-16 lg:py-24 bg-slate-50" ref={ref}>
      <div className="section-container">
        {/*
          Дві колонки від lg: заголовок і прямі контакти ліворуч, форма
          праворуч. Нижче lg — усе в один стовпець у тому ж порядку: заголовок
          короткий, і до поля «Ім'я» звідти один екран.
        */}
        <div className="grid gap-10 lg:gap-14 lg:grid-cols-12 items-center">
          <motion.div
            className="lg:col-span-5"
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <SectionHeading
              title="Контакти"
              caption="Консультація юриста з медичного права"
            />
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-md">
              Розкажіть, на якому ви етапі, — і ми скажемо, що знадобиться саме вам, у які строки та за яких умов.
            </p>
            <DirectContact />
          </motion.div>

          {form}
        </div>
      </div>
    </section>
  );
};

export default Contact;
